/**
 * ReVerifyCardBanner
 * Small display-only banner shown above a fresh card-entry form when we already
 * have a saved last-4 on file. Reframes cold re-entry as a quick security
 * re-verification: "please re-enter your {brand} card ending in ••{last4}".
 *
 * Purely presentational — renders nothing when there's no saved card. It does NOT
 * touch any payment/attach/charge logic; the card the user types is still what
 * gets tokenized and charged on the new Stripe account.
 *
 * Styled with the Braydin (olive/cream) palette; copy is brand-neutral.
 */

import { formatCardBrand } from '../../hooks/usePreviousCard'
import type { PreviousCard } from '../../types/paymentMethod'

interface ReVerifyCardBannerProps {
  card: PreviousCard | null
  /** Optional darker styling for dark-sheet modals (e.g. TipPaymentModal). */
  variant?: 'light' | 'dark'
  className?: string
}

export default function ReVerifyCardBanner({ card, variant = 'light', className = '' }: ReVerifyCardBannerProps) {
  if (!card?.last4) return null

  const brand = formatCardBrand(card.brand)
  const isDark = variant === 'dark'

  return (
    <div
      className={`rounded-lg p-3 mb-3 border ${
        isDark ? 'bg-white/5 border-white/10' : 'bg-braydin-cream border-braydin-sand'
      } ${className}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <svg
          className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-braydin-sage' : 'text-braydin-olive'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 11c0-1.657 1.343-3 3-3s3 1.343 3 3M5 11h14a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2z"
          />
        </svg>
        <span
          className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ${
            isDark ? 'bg-white/10 text-white' : 'bg-white text-braydin-olive border border-braydin-sand'
          }`}
        >
          {brand} ••{card.last4}
        </span>
      </div>
      <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-braydin-ink/70'}`}>
        For your security, please re-enter your {brand} card ending in ••{card.last4} to continue.
      </p>
    </div>
  )
}
